/**
 * <AutonomyBadge /> — Auto / Drafted / Escalated pill.
 *
 * Per DESIGN_SYSTEM.md §6 + §9:
 *   - Auto      (green): action ran with no approval required.
 *   - Drafted   (amber): action created a draft requiring HR approval.
 *   - Escalated (red):   AI couldn't decide and asked HR.
 *
 * Shown on every AI action card in the activity stream. The same
 * action might be Auto in Aggressive mode, Drafted in Standard,
 * Escalated in Conservative — this badge makes the relationship
 * between autonomy mode and observed behavior transparent.
 */

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export type Autonomy = "auto" | "drafted" | "escalated";

export interface AutonomyBadgeProps {
  outcome: Autonomy;
  className?: string;
}

const TONE: Record<Autonomy, { bg: string; fg: string }> = {
  auto: {
    bg: "bg-state-approved-bg",
    fg: "text-state-approved",
  },
  drafted: {
    bg: "bg-state-pending-bg",
    fg: "text-state-pending",
  },
  escalated: {
    bg: "bg-state-denied-bg",
    fg: "text-state-denied",
  },
};

const LABEL_KEY: Record<Autonomy, string> = {
  auto: "ai.autonomy_auto",
  drafted: "ai.autonomy_drafted",
  escalated: "ai.autonomy_escalated",
};

export function AutonomyBadge({
  outcome,
  className = "",
}: AutonomyBadgeProps): ReactNode {
  const { t } = useTranslation();
  const tone = TONE[outcome];
  return (
    <span
      className={[
        "inline-flex items-center",
        "rounded-full px-2 py-0.5",
        "text-xs font-medium",
        tone.bg,
        tone.fg,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {t(LABEL_KEY[outcome])}
    </span>
  );
}
